import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import Notification from "../components/common/Notification";
import styles from "./AdminPage.module.css";

const AdminTestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // --- New State for Editing ---
  const [editingId, setEditingId] = useState(null);

  // --- Notification State ---
  const [notification, setNotification] = useState(null);

  const showToast = (message, type = "success") => {
    setNotification({ message, type });
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get("/api/testimonials");
      setTestimonials(res.data);
      setLoading(false);
    } catch (error) {
      showToast("Error fetching testimonials.", "error");
      setLoading(false);
    }
  };

  const [newReview, setNewReview] = useState({
    name: "",
    role: "",
    feedback: "",
    rating: 5,
  });

  const handleChange = (e) => {
    setNewReview({ ...newReview, [e.target.name]: e.target.value });
  };

  // --- 1. Handle Edit Click (Populate Form) ---
  const handleEdit = (testimonial) => {
    setEditingId(testimonial._id);
    setNewReview({
      name: testimonial.name,
      role: testimonial.role,
      feedback: testimonial.feedback,
      rating: testimonial.rating || 5,
    });
    // Scroll to form so user sees it
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --- 2. Cancel Edit Mode ---
  const handleCancelEdit = () => {
    setEditingId(null);
    setNewReview({ name: "", role: "", feedback: "", rating: 5 });
  };

  // --- 3. Updated Submit (Handle Create vs Update) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        // UPDATE Existing
        await axios.put(`/api/testimonials/${editingId}`, newReview);
        showToast("Review updated successfully!", "success");
      } else {
        // CREATE New
        await axios.post("/api/testimonials", newReview);
        showToast("Review published successfully!", "success");
      }

      // Reset Form
      setNewReview({ name: "", role: "", feedback: "", rating: 5 });
      setEditingId(null);
      fetchTestimonials();
    } catch (error) {
      showToast("Failed to save review.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;
    try {
      await axios.delete(`/api/testimonials/${id}`);
      showToast("Review deleted permanently.", "danger");
      fetchTestimonials();

      // If we deleted the item currently being edited, reset the form
      if (editingId === id) {
        handleCancelEdit();
      }
    } catch (error) {
      showToast("Could not delete review.", "error");
    }
  };

  return (
    <div className={styles.adminPage}>
      <Navbar />

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className={styles.adminHero}>
        <h1>Manage Testimonials</h1>
        <Link
          to="/admin/dashboard"
          className={styles.adminBackLink}
          style={{ color: "#fff", textDecoration: "none", marginTop: 0 }}
        >
          &larr; Back to Dashboard
        </Link>
      </div>

      <div className={styles.adminContentContainer}>
        <form className={styles.adminEditForm} onSubmit={handleSubmit}>
          {/* Dynamic Title */}
          <h2>{editingId ? "Edit Review" : "Add Customer Feedback"}</h2>

          <div className={styles.formRowGrid}>
            <div className={styles.formGroup}>
              <label>Customer Name</label>
              <input
                name="name"
                value={newReview.name}
                onChange={handleChange}
                required
                placeholder="e.g. Abebe K."
              />
            </div>
            <div className={styles.formGroup}>
              <label>Role / Title</label>
              <input
                name="role"
                value={newReview.role}
                onChange={handleChange}
                placeholder="e.g. Coffee Lover"
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Feedback</label>
            <textarea
              name="feedback"
              value={newReview.feedback}
              onChange={handleChange}
              required
              rows="3"
              placeholder="Write the review here..."
            />
          </div>

          <div
            className={styles.formActions}
            style={{ display: "flex", gap: "15px" }}
          >
            <button
              type="submit"
              className={`${styles.adminBtn} ${styles.primary}`}
              disabled={submitting}
            >
              {submitting
                ? "Saving..."
                : editingId
                ? "Update Review"
                : "Publish Review"}
            </button>

            {/* Cancel Button (Only shows when editing) */}
            {editingId && (
              <button
                type="button"
                onClick={handleCancelEdit}
                className={styles.adminBtn}
                style={{ backgroundColor: "#555", color: "#fff" }}
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>

        <h2>Published Reviews</h2>
        {loading ? (
          <p>Loading...</p>
        ) : (
          <div className={styles.adminTableContainer}>
            <table className={styles.adminTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Feedback</th>
                  <th style={{ minWidth: "160px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {testimonials.map((t) => (
                  <tr key={t._id}>
                    <td>
                      <strong>{t.name}</strong>
                      <br />
                      <span style={{ fontSize: "0.85rem", color: "#888" }}>
                        {t.role}
                      </span>
                    </td>
                    <td>"{t.feedback}"</td>
                    <td className={styles.tableActions}>
                      {/* --- Edit Button --- */}
                      <button
                        onClick={() => handleEdit(t)}
                        className={styles.adminBtn}
                        style={{
                          marginRight: "10px",
                          backgroundColor: "#444",
                          color: "#fff",
                        }}
                      >
                        Edit
                      </button>

                      {/* --- Delete Button --- */}
                      <button
                        onClick={() => handleDelete(t._id)}
                        className={`${styles.adminBtn} ${styles.danger}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {testimonials.length === 0 && (
                  <tr>
                    <td
                      colSpan="3"
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#666",
                      }}
                    >
                      No reviews yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AdminTestimonialsPage;
